import { lazy, Suspense, useState, useEffect, type ReactNode } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import type { DomainContext } from '@foxet/shared';
import { useAuthStore } from './lib/store';
import { isPlatformRole } from '@foxet/shared';
import { FOXET_LANDING_URL } from './lib/domain-context';
import { initPosthog } from './lib/posthog';

const DashboardLayout = lazy(() => import('./layouts/DashboardLayout').then((m) => ({ default: m.DashboardLayout })));
const AuthLayout = lazy(() => import('./layouts/AuthLayout').then((m) => ({ default: m.AuthLayout })));
const LoginPage = lazy(() => import('./pages/auth/LoginPage').then((m) => ({ default: m.LoginPage })));
const AcceptInvitePage = lazy(() => import('./pages/auth/AcceptInvitePage').then((m) => ({ default: m.AcceptInvitePage })));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));
const ActivatePage = lazy(() => import('./pages/auth/ActivatePage').then((m) => ({ default: m.ActivatePage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const HomePage = lazy(() => import('./pages/HomePage').then((m) => ({ default: m.HomePage })));
const KnowledgePage = lazy(() => import('./pages/KnowledgePage').then((m) => ({ default: m.KnowledgePage })));
const LibraryPage = lazy(() => import('./pages/LibraryPage').then((m) => ({ default: m.LibraryPage })));
const ScriptsPage = lazy(() => import('./pages/ScriptsPage').then((m) => ({ default: m.ScriptsPage })));
const BattlecardsPage = lazy(() => import('./pages/BattlecardsPage').then((m) => ({ default: m.BattlecardsPage })));
// Insights is a four-tab area (components/analytics/AnalyticsTabs.tsx) with a
// section index at /insights — see InsightsIndex below for why the nav points
// at the section rather than at the funnel tab.
const BriefsPage = lazy(() => import('./pages/BriefsPage').then((m) => ({ default: m.BriefsPage })));
const BriefDetailPage = lazy(() => import('./pages/BriefDetailPage').then((m) => ({ default: m.BriefDetailPage })));
const MeetingAnalysisPage = lazy(() => import('./pages/MeetingAnalysisPage').then((m) => ({ default: m.MeetingAnalysisPage })));
const TranscriptsPage = lazy(() => import('./pages/TranscriptsPage').then((m) => ({ default: m.TranscriptsPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const SupportPage = lazy(() => import('./pages/SupportPage').then((m) => ({ default: m.SupportPage })));
const SupportTicketPage = lazy(() => import('./pages/SupportTicketPage').then((m) => ({ default: m.SupportTicketPage })));
// Settings section pages — the middle level of hub -> section page -> drawer
// (docs/claudedocs/requirements/dashboard-ia-2026-09-19.md §2.4).
const SettingsAgentsPage = lazy(() => import('./pages/settings/AgentsPage').then((m) => ({ default: m.AgentsPage })));
const SettingsFollowUpRulesPage = lazy(() => import('./pages/settings/FollowUpRulesPage').then((m) => ({ default: m.FollowUpRulesPage })));
const SettingsSchedulingPage = lazy(() => import('./pages/settings/SchedulingPage').then((m) => ({ default: m.SchedulingPage })));
const SettingsIntegrationsPage = lazy(() => import('./pages/settings/IntegrationsPage').then((m) => ({ default: m.IntegrationsSectionPage })));
const SettingsWorkspacePage = lazy(() => import('./pages/settings/WorkspacePage').then((m) => ({ default: m.WorkspacePage })));
const SettingsStatusPage = lazy(() => import('./pages/settings/SystemStatusPage').then((m) => ({ default: m.SystemStatusPage })));
const SettingsBillingPage = lazy(() => import('./pages/settings/BillingPage').then((m) => ({ default: m.BillingPage })));
// Chrome-less: the printable invoice (and credit note, same posture) mount
// OUTSIDE DashboardLayout so the sidebar and header never reach the paper.
const BillingInvoicePrintPage = lazy(() => import('./pages/settings/BillingInvoicePrintPage').then((m) => ({ default: m.BillingInvoicePrintPage })));
const BillingCreditNotePrintPage = lazy(() => import('./pages/settings/BillingCreditNotePrintPage').then((m) => ({ default: m.BillingCreditNotePrintPage })));

const ProductsPage = lazy(() => import('./pages/ProductsPage').then((m) => ({ default: m.ProductsPage })));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage').then((m) => ({ default: m.ProductDetailPage })));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage').then((m) => ({ default: m.OnboardingPage })));
const MeetingRoomPage = lazy(() => import('./pages/MeetingRoomPage').then((m) => ({ default: m.MeetingRoomPage })));
const MeetingJoinPage = lazy(() => import('./pages/MeetingJoinPage').then((m) => ({ default: m.MeetingJoinPage })));
const ManageBookingPage = lazy(() => import('./pages/ManageBookingPage').then((m) => ({ default: m.ManageBookingPage })));
const SupervisorConsolePage = lazy(() => import('./pages/SupervisorConsolePage').then((m) => ({ default: m.SupervisorConsolePage })));
const BookDemoPage = lazy(() => import('./pages/BookDemoPage').then((m) => ({ default: m.BookDemoPage })));
const BookTeamPage = lazy(() => import('./pages/BookTeamPage').then((m) => ({ default: m.BookTeamPage })));
const TestOverlayPage = lazy(() => import('./pages/TestOverlayPage').then((m) => ({ default: m.TestOverlayPage })));
const TestCaptionsPage = lazy(() => import('./pages/TestCaptionsPage').then((m) => ({ default: m.TestCaptionsPage })));
const AnalyticsDemosPage = lazy(() => import('./pages/AnalyticsDemosPage').then((m) => ({ default: m.AnalyticsDemosPage })));
const AnalyticsFunnelPage = lazy(() => import('./pages/AnalyticsFunnelPage').then((m) => ({ default: m.AnalyticsFunnelPage })));
const SignalsPage = lazy(() => import('./pages/SignalsPage').then((m) => ({ default: m.SignalsPage })));
const FlowHealthPage = lazy(() => import('./pages/FlowHealthPage').then((m) => ({ default: m.FlowHealthPage })));
const DemoDetailPage = lazy(() => import('./pages/DemoDetailPage').then((m) => ({ default: m.DemoDetailPage })));
import { FlowStudioRedirect } from './pages/StudioRedirects';

const StudioPage = lazy(() => import('./pages/StudioPage').then((m) => ({ default: m.StudioPage })));
const DomainHomePage = lazy(() => import('./pages/DomainHomePage').then((m) => ({ default: m.DomainHomePage })));
const PublicLandingPage = lazy(() => import('./pages/PublicLandingPage').then((m) => ({ default: m.PublicLandingPage })));
const TemplatePreviewPage = lazy(() => import('./pages/demo-templates/PreviewPage').then((m) => ({ default: m.TemplatePreviewPage })));
const FullOnboardingPage = lazy(() => import('./pages/FullOnboardingPage'));
const LandingPagesPage = lazy(() => import('./pages/LandingPagesPage').then((m) => ({ default: m.LandingPagesPage })));
const TargetDetailPage = lazy(() => import('./pages/TargetDetailPage').then((m) => ({ default: m.TargetDetailPage })));
const LeadsPage = lazy(() => import('./pages/LeadsPage').then((m) => ({ default: m.LeadsPage })));
const CheckoutCallbackPage = lazy(() => import('./pages/billing/CheckoutCallbackPage'));

/** `/insights` is the section; `/insights/funnel` is its first tab. */
function InsightsIndex() {
  const { search } = useLocation();
  return <Navigate to={`/insights/funnel${search}`} replace />;
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => !!s.accessToken);
  const hydrate = useAuthStore((s) => s.hydrate);
  useEffect(() => {
    // This is the ONLY route guard every dashboard page passes through.
    // PostHog is never initialized outside this call (plus setAuth, for the
    // login-instant case) — see lib/posthog.ts for why that's the point.
    if (isAuthenticated) { initPosthog(); void hydrate(); }
  }, [isAuthenticated, hydrate]);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function OnboardingGate({ children }: { children: ReactNode }) {
  const tenant = useAuthStore((s) => s.tenant);
  const [checked, setChecked] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    // Platform roles (support/ops) live in the product-less `foxet-platform` tenant;
    // sending them to product onboarding is wrong (2026-09-17, owner landed on
    // /onboarding as platform_admin). The gate is about a customer's first product.
    if (isPlatformRole(useAuthStore.getState().user?.role ?? '')) {
      setNeedsOnboarding(false);
      setChecked(true);
      return;
    }
    // Always re-read from store (not stale closure) to catch markOnboardingComplete() calls
    const current = useAuthStore.getState().tenant;
    if (current?.settings?.onboardingCompleted) {
      setNeedsOnboarding(false);
      setChecked(true);
      return;
    }
    // Check if user has any products — if yes, skip onboarding
    fetch('/api/products', {
      headers: { Authorization: `Bearer ${useAuthStore.getState().accessToken}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then((products: any[]) => {
        // Re-check store in case it was updated while fetch was in-flight
        const latest = useAuthStore.getState().tenant;
        if (latest?.settings?.onboardingCompleted) {
          setNeedsOnboarding(false);
        } else {
          setNeedsOnboarding(products.length === 0);
        }
        setChecked(true);
      })
      .catch(() => setChecked(true));
  }, [tenant]);

  if (!checked) return <div className="flex min-h-screen items-center justify-center"><div className="h-6 w-6 animate-spin rounded-full border-2 border-line-strong border-t-[#09090B]" /></div>;
  if (needsOnboarding) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
}

/**
 * What a custom domain shows when the host resolves to nothing we serve —
 * unknown hostname, a `tenant_domains` row that was never provisioned, or the
 * API being unreachable at boot.
 *
 * It must NOT fall through to the dashboard router: on a customer's domain
 * that would show Foxet's login page to their prospects. It redirects to the
 * Foxet landing (the roadmap's stated fail-closed target) and renders a
 * neutral frame with a manual link for the case where the redirect is blocked.
 * No tenant name, no product, nothing borrowed from a near-miss lookup.
 */
function UnconfiguredDomain({ host }: { host: string }) {
  useEffect(() => {
    const t = setTimeout(() => window.location.replace(FOXET_LANDING_URL), 1200);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-sunken px-6 text-center">
      <p className="text-small font-semibold text-ink">This domain isn’t set up yet</p>
      <p className="max-w-sm text-caption text-faint">
        <span className="font-mono">{host}</span> doesn’t point at a published demo. Taking you to Foxet…
      </p>
      <a href={FOXET_LANDING_URL} className="text-caption font-medium text-accent hover:underline">
        Go to foxet.ai
      </a>
    </div>
  );
}

/**
 * The route table a tenant's custom domain gets. Deliberately tiny — this
 * origin is a demo surface, not the dashboard, and every dashboard route that
 * isn't here is unreachable on it.
 *
 * `/` mounts the live-demo front door (DomainHomePage) when a product is
 * bound to the domain. The captured-walkthrough player that used to share this
 * door was removed (walkthroughs feature removal, 2026-10) — see
 * DomainHomePage.tsx for what the door offers now.
 */
function CustomDomainRoutes({ ctx }: { ctx: DomainContext }) {
  return (
    <Routes>
      <Route
        path="/"
        element={
          ctx.productSlug ? (
            // It used to drop straight into the walkthrough player, which made
            // the /book and /meeting routes below unreachable from the domain.
            <DomainHomePage ctx={ctx} />
          ) : (
            // Honest dead end, not a fake chooser: the domain is live but bound
            // to no product and the tenant has 0 or 2+ published products, so
            // there is nothing to pick from without guessing.
            <UnboundDomain host={ctx.host} />
          )
        }
      />
      <Route path="/book/:productSlug" element={<BookDemoPage />} />
      {/* Team booking is per-product (owner, 2026-10-19): every tenant's
          product can offer it, so — like /book above — it MUST be here as
          well as in the platform table below. A prospect on this product's
          own custom domain books with this product's staff; the route can't
          exist on only one origin. */}
      <Route path="/talk/:productSlug" element={<BookTeamPage />} />
      {/* The booking-email corridor. It MUST be here as well as in the platform
          table below: a prospect who books on this domain is emailed links on
          this domain, and a route that exists on only one origin is a 404 for
          exactly the tenants who bought a custom domain.
          `/meeting/join` is listed before `/meeting/:id` for readability only —
          react-router ranks a static segment above a dynamic one regardless. */}
      <Route path="/meeting/join" element={<MeetingJoinPage />} />
      <Route path="/meeting/:id" element={<MeetingRoomPage />} />
      <Route path="/scheduler/manage/:token" element={<ManageBookingPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** Live domain, nothing bound to it. Says so instead of guessing a product. */
function UnboundDomain({ host }: { host: string }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-sunken px-6 text-center">
      <p className="text-small font-semibold text-ink">No demo is published here yet</p>
      <p className="max-w-sm text-caption text-faint">
        <span className="font-mono">{host}</span> is connected, but it isn’t pointed at a published product. The
        account owner can pick one in Settings → Workspace.
      </p>
    </div>
  );
}

export function App({ domainContext }: { domainContext?: DomainContext | null }) {
  if (domainContext) {
    return (
      <Suspense fallback={<div className="flex min-h-screen items-center justify-center bg-sunken"><div className="h-6 w-6 animate-spin rounded-full border-2 border-line-strong border-t-[#09090B] dark:border-t-[#FAFAFA]" /></div>}>
        {domainContext.tenantSlug
          ? <CustomDomainRoutes ctx={domainContext} />
          : <UnconfiguredDomain host={domainContext.host} />}
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<div className="flex min-h-screen items-center justify-center bg-sunken"><div className="h-6 w-6 animate-spin rounded-full border-2 border-line-strong border-t-[#09090B] dark:border-t-[#FAFAFA]" /></div>}>
      <Routes>
        {/* Always redirect to Home — landing page is now a separate app */}
        <Route path="/" element={<Navigate to="/home" replace />} />

        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        </Route>

        {/* Activate — standalone split-screen (outside AuthLayout) */}
        <Route path="/activate/:token" element={<ActivatePage />} />

        {/* Meeting room (public - no auth required for customers).
            /meeting/join exchanges a booking's confirmation token for the
            meeting and redirects here; both are mirrored in CustomDomainRoutes
            above, because both are emailed to prospects on whichever origin
            they booked from. */}
        <Route path="/meeting/join" element={<MeetingJoinPage />} />
        <Route path="/meeting/:id" element={<MeetingRoomPage />} />

        {/* Public booking page + manage-your-booking (no auth required — the
            confirmation token is the capability) */}
        <Route path="/book/:productSlug" element={<BookDemoPage />} />
        <Route path="/scheduler/manage/:token" element={<ManageBookingPage />} />

        {/* Team booking (owner, 2026-10-19): a per-product capability, not a
            Foxet-only sales funnel — any tenant's product can offer it.
            Mirrored into CustomDomainRoutes above for the same reason /book
            is: a prospect on a tenant's own custom domain needs this route
            on that origin too. */}
        <Route path="/talk/:productSlug" element={<BookTeamPage />} />

        {/* Template preview (must be before dynamic :tenantSlug routes) */}
        <Route path="/l/preview" element={<TemplatePreviewPage />} />

        {/* Public demo landing pages (no auth required) */}
        <Route path="/l/:tenantSlug/:pageSlug" element={<PublicLandingPage />} />
        <Route path="/l/:tenantSlug" element={<PublicLandingPage />} />

        {/* The printable invoice — authenticated, but deliberately outside
            DashboardLayout: no sidebar, no header, nothing that would land
            on the paper. Same posture as the meeting room, also full-bleed
            by design. */}
        <Route
          path="/settings/billing/invoices/:id/print"
          element={<ProtectedRoute><BillingInvoicePrintPage /></ProtectedRoute>}
        />

        {/* Same posture, for the credit note the drawer's list opens. */}
        <Route
          path="/settings/billing/credit-notes/:id/print"
          element={<ProtectedRoute><BillingCreditNotePrintPage /></ProtectedRoute>}
        />

        {/* Test page for overlay preview (dev only) */}
        <Route path="/test/overlay" element={<TestOverlayPage />} />
        <Route path="/test/captions" element={<TestCaptionsPage />} />

        {/* Onboarding (full-page, no sidebar — protected) */}
        <Route path="/onboarding" element={<ProtectedRoute><FullOnboardingPage /></ProtectedRoute>} />

        {/* A person's own surfaces, OUTSIDE the onboarding gate.
            A user with no product yet still has a name, a theme, a password
            — and, most of all, the right to ask for help. Gating /profile
            behind "create a product first" sent them to /onboarding when they
            clicked My profile (found in the browser, 2026-09-18); the same
            was true of /support, and a person blocked DURING onboarding is
            exactly the person who needs to raise a ticket. The gate is about
            a workspace's first product; it has nothing to say about either. */}
        <Route
          element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}
        >
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/support/:id" element={<SupportTicketPage />} />
        </Route>

        {/* Dashboard routes */}
        <Route
          element={
            <ProtectedRoute>
              <OnboardingGate>
                <DashboardLayout />
              </OnboardingGate>
            </ProtectedRoute>
          }
        >
          <Route path="/home" element={<HomePage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/:id" element={<ProductDetailPage />} />
          <Route path="/products/:id/onboarding" element={<OnboardingPage />} />
          {/* The unified studio is the only flow/walkthrough editor; the two
              pre-merge URLs redirect into it (see pages/StudioRedirects.tsx). */}
          <Route path="/products/:id/flow-studio" element={<FlowStudioRedirect />} />
          <Route path="/products/:id/studio" element={<StudioPage />} />
          <Route path="/landing-pages" element={<LandingPagesPage />} />
          <Route path="/knowledge" element={<KnowledgePage />} />
          <Route path="/knowledge/scripts" element={<ScriptsPage />} />
          <Route path="/knowledge/battlecards" element={<BattlecardsPage />} />
          {/* The SECTION index. The nav points at /insights, not at the funnel
              leaf, so NavLink marks Insights active on all four tabs — pointed
              at /insights/funnel it went inactive the moment you opened Briefs,
              Sessions or Demand signals, which is three quarters of the section
              with nothing selected in the rail. This is an in-section index, not
              a legacy redirect: it carries the ?productId scope through rather
              than dropping the one thing every Insights page is scoped by. */}
          <Route path="/insights" element={<InsightsIndex />} />
          <Route path="/insights/funnel" element={<AnalyticsFunnelPage />} />
          <Route path="/insights/briefs" element={<BriefsPage />} />
          <Route path="/insights/briefs/:id" element={<BriefDetailPage />} />
          <Route path="/meetings/:id/analysis" element={<MeetingAnalysisPage />} />
          <Route path="/insights/sessions" element={<AnalyticsDemosPage />} />
          <Route path="/insights/signals" element={<SignalsPage />} />
          <Route path="/insights/flow-health" element={<FlowHealthPage />} />
          {/* IN-SECTION redirect, not a legacy one: the self-demo D7 flow was
              re-authored against /insights/demand-signals days before the
              rename, and the "no redirects" rule is about the retired
              top-level nouns, not about a tab renamed under Insights. */}
          <Route path="/insights/demand-signals" element={<Navigate to="/insights/signals" replace />} />
          <Route path="/insights/sessions/:sessionId" element={<DemoDetailPage />} />
          {/* Leads — the single daily page (dashboard-ia-2026-09-19 §2.2). It
              replaced /targets, /demos, /follow-ups and /ghost, all removed in
              the 2026-09-18 sweep. No redirects: the owner's call — "absolutely
              zero risk because we don't have any clients on production". */}
          <Route path="/leads" element={<LeadsPage />} />
          {/* The detail is a DRAWER over the list (owner, 2026-09-19), so this
              route renders the same page with it open — the link still works,
              and closing it returns to the list underneath. */}
          <Route path="/leads/:id" element={<LeadsPage />} />
          {/* Library — a main-nav destination again (owner, 2026-09-21): "Library
              in the main menu was better." It spent two days as the Settings
              "Documents" section (dashboard-ia-2026-09-19 §2.4); that route now
              redirects here rather than 404ing on an old bookmark or KPI link. */}
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/supervisor" element={<SupervisorConsolePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/agents" element={<SettingsAgentsPage />} />
          <Route path="/settings/documents" element={<Navigate to="/library" replace />} />
          <Route path="/settings/follow-ups" element={<SettingsFollowUpRulesPage />} />
          <Route path="/settings/availability" element={<SettingsSchedulingPage />} />
          <Route path="/settings/integrations" element={<SettingsIntegrationsPage />} />
          <Route path="/settings/workspace" element={<SettingsWorkspacePage />} />
          <Route path="/settings/status" element={<SettingsStatusPage />} />
          <Route path="/settings/billing" element={<SettingsBillingPage />} />
          <Route path="/billing/checkout/callback" element={<CheckoutCallbackPage />} />
          {/* Anything else inside the shell — including the eleven routes the
              2026-09-18 sweep removed — lands here rather than on a blank page. */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
